import { DropdownNew, DropdownSubmenuItem, LuneThemeNew, Text } from '@lune-fe/lune-ui-lib'
import AccountBalanceWalletOutlinedIcon from '@mui/icons-material/AccountBalanceWalletOutlined'
import FilterListOutlinedIcon from '@mui/icons-material/FilterListOutlined'
import GroupOutlinedIcon from '@mui/icons-material/GroupOutlined'
import Box from '@mui/material/Box'
import { Dispatch, ReactNode, SetStateAction } from 'react'
import { Range } from 'react-date-range'

import AccountMultiselectFilter from 'components/AccountMultiselectFilter'
import DateRangeFilterNew, { PredefinedRange } from 'components/DateRangeFilterNew'
import MultiselectFilterNew from 'components/MultiselectFilterNew'

export interface FilterConfig<T> {
    filterName: string
    options: T[]
    preselected: T[]
    setSelectedOptions: Dispatch<SetStateAction<T[]>>
    transformLabel?: (label: T) => string
    /**
     * If true, only one option can be selected.
     * Defaults to false (multi-select).
     */
    singleSelect?: boolean
    filterIcon?: ReactNode
    /**
     * If true, displays a search input to filter options.
     * Defaults to false.
     */
    enableSearch?: boolean
    searchPlaceholder?: string
}

export interface FilterProps<T> {
    preselectedDateRange?: Range
    setDateRange: Dispatch<SetStateAction<Range | undefined>>
    preselectedAccountIds?: string[]
    setSelectedAccountIds?: Dispatch<SetStateAction<string[]>>
    // Readonly array with never type - TypeScript allows contravariant assignment
    filters: FilterConfig<T>[]
    defaultDateRange?: PredefinedRange
    /**
     * If true, renders the filters as standalone dropdowns instead of sub-items of the filter dropdown
     * Defaults to false.
     */
    flatFilters?: boolean
}

const FiltersBoxNew = <T,>({
    preselectedDateRange,
    setDateRange,
    preselectedAccountIds,
    setSelectedAccountIds,
    filters,
    defaultDateRange,
    flatFilters,
}: FilterProps<T>) => {
    const { spacing } = LuneThemeNew

    const sharedMenuSx = {
        maxHeight: '550px',
        width: '288px',
        '.MuiList-root': {
            pt: 0,
            pb: 0,
        },
    }

    const sharedMenuItemSx = {
        gap: 2,
        minHeight: '48px !important',
        '.MuiListItemIcon-root': {
            minWidth: 'auto',
        },
    }

    return (
        <Box sx={{ display: 'flex', gap: spacing(1), flexWrap: 'wrap' }}>
            <DateRangeFilterNew
                dateDisplayFormat="MMM D YYYY"
                defaultRange={defaultDateRange}
                setDateRange={setDateRange}
                predefinedDateRange={preselectedDateRange}
            />
            {flatFilters ? (
                <>
                    {/* Account filter - always shown first */}
                    {preselectedAccountIds && setSelectedAccountIds && (
                        <DropdownNew
                            buttonSize={'sm'}
                            buttonVariant={'secondary'}
                            buttonStyle={'fill'}
                            buttonLabel={
                                <Text variant={'body3'} sx={{ textTransform: 'capitalize' }}>
                                    Client account
                                </Text>
                            }
                            leftIcon={<GroupOutlinedIcon />}
                            sx={{
                                display: 'flex',
                                justifyContent: 'flex-start',
                            }}
                        >
                            <AccountMultiselectFilter
                                preselectedIds={preselectedAccountIds}
                                setSelectedIds={setSelectedAccountIds}
                            />
                        </DropdownNew>
                    )}
                    {/* Other filters */}
                    {filters.map((filter, index) => (
                        <DropdownNew
                            key={index}
                            buttonSize={'sm'}
                            buttonVariant={'secondary'}
                            buttonStyle={'fill'}
                            buttonLabel={
                                <Text variant={'body3'} sx={{ textTransform: 'capitalize' }}>
                                    {filter.filterName}
                                </Text>
                            }
                            leftIcon={filter.filterIcon}
                            sx={{
                                display: 'flex',
                                justifyContent: 'flex-start',
                            }}
                        >
                            <MultiselectFilterNew
                                preselected={filter.preselected}
                                setSelectedOptions={filter.setSelectedOptions}
                                singleSelect={filter.singleSelect}
                                options={filter.options}
                                transformLabel={filter.transformLabel}
                                filterName={filter.filterName}
                                enableSearch={filter.enableSearch}
                                searchPlaceholder={filter.searchPlaceholder}
                            />
                        </DropdownNew>
                    ))}
                </>
            ) : filters.length ? (
                <DropdownNew
                    buttonSize={'sm'}
                    buttonVariant={'secondary'}
                    buttonStyle={'fill'}
                    closeOnClick={false}
                    buttonLabel={'Filter'}
                    leftIcon={<FilterListOutlinedIcon />}
                    sx={{
                        display: 'flex',
                        justifyContent: 'flex-start',
                    }}
                >
                    {/* Account filter - always shown first */}
                    {preselectedAccountIds && setSelectedAccountIds && (
                        <DropdownSubmenuItem
                            key="account-filter"
                            itemSx={sharedMenuItemSx}
                            label={
                                <Text variant={'body3'} sx={{ textTransform: 'capitalize' }}>
                                    Client account
                                </Text>
                            }
                            leftIcon={<AccountBalanceWalletOutlinedIcon />}
                            menuSx={sharedMenuSx}
                        >
                            <AccountMultiselectFilter
                                preselectedIds={preselectedAccountIds}
                                setSelectedIds={setSelectedAccountIds}
                            />
                        </DropdownSubmenuItem>
                    )}
                    {/* Other filters */}
                    {filters.map((filter, index) => (
                        <DropdownSubmenuItem
                            key={`${filter.filterName}-${index}`}
                            itemSx={sharedMenuItemSx}
                            label={
                                <Text variant={'body3'} sx={{ textTransform: 'capitalize' }}>
                                    {filter.filterName}
                                </Text>
                            }
                            leftIcon={filter.filterIcon}
                            menuSx={sharedMenuSx}
                        >
                            <MultiselectFilterNew
                                preselected={filter.preselected}
                                setSelectedOptions={filter.setSelectedOptions}
                                singleSelect={filter.singleSelect}
                                options={filter.options}
                                transformLabel={filter.transformLabel}
                                filterName={filter.filterName}
                                enableSearch={filter.enableSearch}
                                searchPlaceholder={filter.searchPlaceholder}
                            />
                        </DropdownSubmenuItem>
                    ))}
                </DropdownNew>
            ) : (
                <></>
            )}
        </Box>
    )
}

export default FiltersBoxNew
